import axios from 'axios'

class PushNotificationService {
  constructor() {
    this.swRegistration = null
  }

  checkSupport() {
    const status = this.getSupportStatus()
    return status.supported
  }

  getSupportStatus() {
    const ua = navigator.userAgent || ''
    const isSWSupported = 'serviceWorker' in navigator
    const isPushSupported =
      'PushManager' in window ||
      (typeof ServiceWorkerRegistration !== 'undefined' &&
        'pushManager' in ServiceWorkerRegistration.prototype)
    const hasNotification = 'Notification' in window

    const isStandalone = !!(
      (navigator && navigator.standalone) ||
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    )

    const isIOS =
      (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) &&
      !window.MSStream
    const isAndroid = /Android/.test(ua)
    const isInAppBrowser = isAndroid && /; wv\)/i.test(ua)

    return {
      supported: isSWSupported && isPushSupported && hasNotification,
      isSWSupported,
      isPushSupported,
      hasNotification,
      isStandalone,
      isIOS,
      isInAppBrowser,
      needsStandalone: isIOS && !isStandalone
    }
  }

  registerServiceWorker() {
    if (!this.checkSupport()) {
      return Promise.resolve(null)
    }

    if (this.swRegistration && this.swRegistration.active) {
      return Promise.resolve(this.swRegistration)
    }

    return navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        const swUrl = new URL(`${process.env.BASE_URL}service-worker.js`, window.location.origin)
          .href
        const pushReg = registrations.find((reg) => reg.active && reg.active.scriptURL === swUrl)

        if (pushReg) {
          this.swRegistration = pushReg
          return pushReg
        }

        return navigator.serviceWorker
          .register(swUrl)
          .then(() => {
            return navigator.serviceWorker.ready
          })
          .then((reg) => {
            this.swRegistration = reg
            return reg
          })
      })
      .catch((error) => {
        throw error
      })
  }

  isReady() {
    if (!this.checkSupport()) return Promise.resolve(false)
    return navigator.serviceWorker.ready.then((reg) => {
      if (reg && reg.active && reg.active.scriptURL.includes('service-worker.js')) {
        this.swRegistration = reg
        return true
      }
      return navigator.serviceWorker.getRegistrations().then((registrations) => {
        const pushReg = registrations.find(
          (r) => r.active && r.active.scriptURL.includes('service-worker.js')
        )
        if (pushReg) {
          this.swRegistration = pushReg
          return true
        }
        return false
      })
    })
  }

  requestPermission() {
    if (!('Notification' in window)) {
      return Promise.reject(new Error('Notification API not supported'))
    }
    return window.Notification.requestPermission().then((permission) => {
      if (permission !== 'granted') throw new Error('Permission denied')
      return permission
    })
  }

  getSubscription() {
    return this.registerServiceWorker().then((reg) => {
      if (!reg) return null
      return reg.pushManager.getSubscription()
    })
  }

  checkSubscription() {
    return this.getSubscription().then((subscription) => {
      if (!subscription) return false
      return axios
        .post('/push-subscription/check', {
          endpoint: subscription.endpoint
        })
        .then((response) => Boolean(response?.data?.exists ?? response?.data?.Exists))
    })
  }

  subscribe() {
    return this.registerServiceWorker()
      .then(() => axios.get('/public/push-subscription/vapid-public-key'))
      .then((response) => {
        const convertedVapidKey = this.urlBase64ToUint8Array(response.data.publicKey)
        return this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey
        })
      })
      .then((subscription) => {
        const payload = {
          endpoint: subscription.endpoint,
          p256dh: this.arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: this.arrayBufferToBase64(subscription.getKey('auth'))
        }
        return axios.post('/push-subscription/subscribe', payload).then(() => subscription)
      })
  }

  unsubscribe() {
    return this.getSubscription().then((subscription) => {
      if (subscription) {
        return subscription.unsubscribe().then(() => {
          return axios.post('/push-subscription/unsubscribe', {
            endpoint: subscription.endpoint
          })
        })
      }
    })
  }

  arrayBufferToBase64(buffer) {
    const binary = String.fromCharCode.apply(null, new Uint8Array(buffer))
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}

export const pushNotificationService = new PushNotificationService()
export default pushNotificationService
